import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useTheme } from '../contexts/ThemeContext'

interface HistoryItem {
  id: string
  video_id: string
  watched_at: string
  watch_duration: number
  completed: boolean
  video: {
    title: string
    thumbnail_url: string
    content_type: string
    duration: number
    views: number
  }
}

interface Props {
  currentUser: { id: string; username: string } | null
}

const fmtTimeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime()
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const day = Math.floor(diff / 86400000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  return `${day}d ago`
}

const TYPE_COLOR: Record<string, string> = {
  movie: '#c084fc', series: '#60a5fa', short_drama: '#fb923c', podcast: '#34d399'
}

const WatchHistoryScreen: React.FC<Props> = ({ currentUser }) => {
  const navigate = useNavigate()
  const { isDark } = useTheme()
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'completed' | 'in_progress'>('all')

  const C = {
    bg: isDark ? '#080810' : '#f0f2f8',
    surface: isDark ? 'rgba(255,255,255,.03)' : '#fff',
    border: isDark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)',
    text: isDark ? '#fff' : '#0f172a',
    sub: isDark ? '#666' : '#94a3b8',
  }

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return }
    fetchHistory()
  }, [currentUser, filter])

  const fetchHistory = async () => {
    if (!currentUser) return
    setLoading(true)
    try {
      let query = supabase
        .from('watch_history')
        .select('*, video:videos(title, thumbnail_url, content_type, duration, views)')
        .eq('user_id', currentUser.id)
        .order('watched_at', { ascending: false })
        .limit(50)

      if (filter === 'completed') {
        query = query.eq('completed', true)
      } else if (filter === 'in_progress') {
        query = query.eq('completed', false)
      }

      const { data, error } = await query
      if (error) throw error
      setHistory(data || [])
    } catch (e) {
      console.error('Error:', e)
    } finally {
      setLoading(false)
    }
  }

  const removeFromHistory = async (id: string) => {
    await supabase.from('watch_history').delete().eq('id', id)
    fetchHistory()
  }

  const clearAllHistory = async () => {
    if (!window.confirm('Clear all watch history?')) return
    if (!currentUser) return
    await supabase.from('watch_history').delete().eq('user_id', currentUser.id)
    fetchHistory()
  }

  return (
    <div style={{ minHeight: '100vh', background: C.bg }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', color: C.sub, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}
            >
              ← Back
            </button>
            <h1 style={{ color: C.text, fontSize: 24, fontWeight: 900, margin: 0, fontFamily: "'Syne',sans-serif" }}>
              📺 Watch History
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={clearAllHistory}
              style={{
                padding: '8px 16px', borderRadius: 10,
                border: '1px solid rgba(229,57,53,.3)',
                background: 'transparent', color: '#e53935',
                fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[
            { value: 'all', label: 'All' },
            { value: 'completed', label: 'Completed' },
            { value: 'in_progress', label: 'In Progress' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value as any)}
              style={{
                padding: '6px 16px', borderRadius: 20,
                border: filter === f.value ? '1px solid #e53935' : `1px solid ${C.border}`,
                background: filter === f.value ? 'rgba(229,57,53,.1)' : 'transparent',
                color: filter === f.value ? '#e53935' : C.sub,
                fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* History List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{
              width: 36, height: 36,
              border: '2px solid #e53935', borderTopColor: 'transparent',
              borderRadius: '50%', animation: 'spin .8s linear infinite',
              margin: '0 auto'
            }} />
          </div>
        ) : history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📺</div>
            <p style={{ color: C.sub, fontSize: 15 }}>
              {filter === 'all' ? 'No watch history yet' : `No ${filter.replace('_', ' ')} videos`}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {history.map(item => (
              <div
                key={item.id}
                style={{
                  display: 'flex', gap: 14,
                  padding: 12, borderRadius: 14,
                  background: C.surface, border: `1px solid ${C.border}`,
                }}
              >
                <Link to={`/video/${item.video_id}`} style={{ flexShrink: 0 }}>
                  <img
                    src={item.video?.thumbnail_url}
                    alt=""
                    style={{
                      width: 160, height: 90,
                      borderRadius: 8, objectFit: 'cover'
                    }}
                  />
                </Link>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link
                    to={`/video/${item.video_id}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <h3 style={{
                      color: C.text, fontSize: 14, fontWeight: 700,
                      margin: '0 0 4px', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {item.video?.title}
                    </h3>
                  </Link>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                    <span style={{
                      padding: '1px 8px', borderRadius: 6,
                      background: TYPE_COLOR[item.video?.content_type || ''] + '20',
                      color: TYPE_COLOR[item.video?.content_type || ''],
                      fontSize: 10, fontWeight: 700
                    }}>
                      {item.video?.content_type?.replace('_', ' ')}
                    </span>
                    <span style={{ color: C.sub, fontSize: 11 }}>
                      👁 {item.video?.views?.toLocaleString() || 0} views
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Progress bar */}
                    <div style={{
                      flex: 1, height: 3,
                      background: isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.1)',
                      borderRadius: 2, overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${item.video?.duration ? Math.min((item.watch_duration / item.video.duration) * 100, 100) : 0}%`,
                        background: item.completed ? '#22c55e' : '#e53935',
                        borderRadius: 2
                      }} />
                    </div>
                    <span style={{ color: C.sub, fontSize: 11 }}>
                      {item.completed ? '✅ Completed' : '▶ In Progress'}
                    </span>
                    <span style={{ color: C.sub, fontSize: 11 }}>
                      {fmtTimeAgo(item.watched_at)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => removeFromHistory(item.id)}
                  style={{
                    background: 'none', border: 'none',
                    color: C.sub, fontSize: 16, cursor: 'pointer',
                    padding: '0 4px', alignSelf: 'flex-start'
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

export default WatchHistoryScreen